import { Component, EventEmitter, Input, Output, ViewChild } from "@angular/core";
import { AbstractControl, NgForm, ValidatorFn, Validators } from "@angular/forms";

import { User } from "src/app/models/user";
import { CHECKOUT_LABEL } from "src/constants/constants";

enum FIELDS {
  FIRST_NAME = <any>'firstName',
  LAST_NAME = <any>'lastName',
  PHONE = <any>'phone',
  EMAIL = <any>'email',
  EMAIL_CONFIRM = <any>'confirmEmail',
  STREET = <any>'street',
  ADDITIONAL_BILLING_ADDRESS_LINE = <any>'additionalBillingAddressLine',
  CITY = <any>'city',
  ZIP_POSTAL_CODE = <any>'zipPostalCode',
  COUNTRY = <any>'country',
  STATE = <any>'state'
}

@Component({
  selector: 'account-form-v2',
  templateUrl: './account-form-v2.html',
  styleUrls: ['./account-form-v2.scss']
})

export class AccountFormV2Component {

  @ViewChild('accountForm') accountForm: NgForm;

  @Input() guest: User = {} as User;
  @Input() countriesList: Array<any> = [];
  @Input() statesList: Array<any> = [];
  @Input() submittedAccountForm: boolean = false;
  @Input() emailToConfirm: string = '';
  @Input() euSpecified : boolean = false;
  @Input() braintreeDefaultZipPostalCode: string = '';
  @Input() braintreeDefaultCountry: string = 'United States';
  @Input() braintreeDefaultState: string = '';

  @Output() linkForm = new EventEmitter<NgForm>();
  @Output() validateAddressAndCalculateTaxes = new EventEmitter<string>();
  @Output() rememberAccountInformation = new EventEmitter<void>();
  @Output() readyToCreateLead = new EventEmitter<void>();
  @Output() emitConfirmEmail = new EventEmitter<string>();
  @Output() countryChange = new EventEmitter();
  @Output() stateChange = new EventEmitter();

  readonly FIELDS = FIELDS;

  emailPattern: string = '^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,4}$';
  textPattern: string = '^[a-zA-Z][a-zA-Z ]*$';
  mixedPattern: string = '^[a-zA-Z\\d][a-zA-Z\\d .,-¡«¿ßäéíñöüÄÉÍÑÖÜàÀÈèÌìòÒùÙáéíóúýÁÉÍÓÚÝ]*$';
  numberPattern: string = '^[\\d]*$';
  phonePattern: string = '[-+*().,0-9x\/\\# ]*'; // TOUC-1036
  addressPattern: string = '[*#\+,.%()^:;=?[\/A-Za-z0-9 -¡«¿ßäéíñöüÄÉÍÑÖÜàÀÈèÌìòÒùÙáéíóúýÁÉÍÓÚÝ]*' ; // TOUC-1036, DEV-35015 adds german and spanish characters

  isInvalidState: boolean = false;
  hideStateByCountryList: Array<string> = [''];

  constructor() {}

  ngAfterViewInit() {
    setTimeout(() => {
      // EMAIL
      this.accountForm.controls[this.FIELDS.EMAIL].setValidators([
        Validators.required,
        Validators.email,
        this.isValidEmails(this),
        this.isConfirmEmails(this)
      ]);
      this.accountForm.controls[this.FIELDS.EMAIL].updateValueAndValidity();

      // EMAIL_CONFIRM
      this.accountForm.controls[this.FIELDS.EMAIL_CONFIRM].setValidators([
        Validators.required,
        Validators.email,
        this.isValidEmails(this),
        this.isConfirmEmails(this)
      ]);
      this.accountForm.controls[this.FIELDS.EMAIL_CONFIRM].updateValueAndValidity();

      this.linkForm.emit(this.accountForm);

    }, 0);
  }

  getLabel(label: string) {
    try {
      if(this.braintreeDefaultCountry && typeof CHECKOUT_LABEL[this.braintreeDefaultCountry] == 'object' && typeof CHECKOUT_LABEL[this.braintreeDefaultCountry][label] == 'string') return CHECKOUT_LABEL[this.braintreeDefaultCountry][label];
    } catch (e) {
    }
    return CHECKOUT_LABEL['United States'][label];
  }

  validate_AddressAndCalculateTaxes(address: string) {
    this.validateAddressAndCalculateTaxes.emit(address);
  }

  remember_AccountInformation() {
    this.rememberAccountInformation.emit();
  }

  ready_ToCreateLead() {
    this.readyToCreateLead.emit();
  }

  emitConfirmEmailToCheckout() {
    this.emitConfirmEmail.emit(this.emailToConfirm);
  }

  validateEmailFields() {
    // Trigger validation on both email fields to keep them synchronized
    if (this.accountForm && this.accountForm.controls) {
      const emailControl = this.accountForm.controls[this.FIELDS.EMAIL];
      const confirmEmailControl = this.accountForm.controls[this.FIELDS.EMAIL_CONFIRM];

      if (emailControl) {
        emailControl.updateValueAndValidity();
      }
      if (confirmEmailControl) {
        confirmEmailControl.updateValueAndValidity();
      }
    }
  }

  isConfirmEmails(scope:any): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      // Get the parent form group
      const parent = control.parent;
      if (!parent) return null;
      
      // Get both email fields
      const emailControl = parent.get(scope.FIELDS.EMAIL);
      const confirmEmailControl = parent.get(scope.FIELDS.EMAIL_CONFIRM);
      
      if (!emailControl || !confirmEmailControl) return null;
      
      const email = emailControl.value;
      const confirmEmail = confirmEmailControl.value;
      
      // If either field is empty, don't validate yet (let required validator handle it)
      if (!email || !confirmEmail) return null;
      
      // Check if emails match (case-insensitive)
      const emailsMatch = email.toLowerCase() === confirmEmail.toLowerCase();
      
      return emailsMatch ? null : {'invalidConfirmEmails': {value: control.value}};
    };
  }

  isValidEmails(scope:any): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} | null => {
      console.log('isValidEmails scope', scope);
      return (control.value && control.value.match(scope.emailPattern))
        ? null
        : {'invalidValidEmail': {value: control.value}};
    };
  }

  isFacebookApp() {
    if((window as any)['facebookAutofillDisabled'])  {
      return false; // this means the FB auto fill isn't going to be used, like  hen the user clicks the paypal button.
    }
    if((window as any)['facebookAutofillComplete'])  {
      return false; // this means the FB auto fill already ran
    }
    let ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    return (ua.indexOf("FBAN") > -1) || (ua.indexOf("FBAV") > -1)

  }

  // Display the state field if it is not included in the hideStateByCountryList
  displayStateField() {
    return !this.hideStateByCountryList.includes(this.guest.country || '');
  }

  onCountryChange() {
    this.countryChange.emit(true);
    //this.confirmAddress();
  }
  onStateChange() {
    this.stateChange.emit(true);
    //this.confirmAddress();
  }
  facebookAppStateChangeHandler() {
    console.log('facebookAppStateChangeHandler');
    try {
      let fbcountry:any = (document.querySelector('#FBCountryCode') as any).value;

      if(!fbcountry || fbcountry == '') fbcountry = 'US';
      let FBStates = ((window as any)['countries']).find(  ({ value }:{[key:string]:any}) => value === fbcountry);
      if(!FBStates) return;
      let FBState = FBStates.states;
      if((document.querySelector('#FBStateCode') as any) && (document.querySelector('#FBStateCode') as any).value.length > 2) {
        // Skip if value not in state list
        if(FBState[(document.querySelector('#FBStateCode') as any).value]) {
          (document.querySelector('#facebookstate') as any).value = (document.querySelector('#FBStateCode') as any).value;
        }
      } else {
        if(FBState[(document.querySelector('#FBStateCode') as any).value]) {
          (document.querySelector('#facebookstate') as any).value = FBState[(document.querySelector('#FBStateCode') as any).value];
        }
      }
      setTimeout(() => {
        if (this.accountForm.form.controls.additionalBillingAddressLine.value == this.accountForm.form.controls.street.value || this.accountForm.form.controls.additionalBillingAddressLine.value == '') {
          if (document.querySelector('#FBAddressLine2') && (document.querySelector('#FBAddressLine2') as any).value != '') {
            this.accountForm.form.controls.additionalBillingAddressLine.patchValue((document.querySelector('#FBAddressLine2') as any).value);
            this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
            this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
          }
        } 
      }, 300);

      if(this.accountForm.form.controls.street.value == this.accountForm.form.controls.additionalBillingAddressLine.value) {
        this.accountForm.form.controls.additionalBillingAddressLine.patchValue('');
        this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
        this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
      }
      if (FBStates.label != '' && FBStates.label != this.accountForm.form.controls.country.value) {
        this.accountForm.form.controls.country.patchValue(FBStates.label);
        this.accountForm.form.controls.country.markAsDirty();
        this.accountForm.form.controls.country.updateValueAndValidity();
        this.accountForm.form.markAsDirty();

        if((document.querySelector('#FBAddressLine1') as any) && (document.querySelector('#FBAddressLine1') as any).value != '') {
          this.accountForm.form.controls.street.patchValue((document.querySelector('#FBAddressLine1') as any).value);
          this.accountForm.form.controls.street.markAsDirty();
          this.accountForm.form.controls.street.updateValueAndValidity();
        }
        if((document.querySelector('#FBAddressLine2') as any) && (document.querySelector('#FBAddressLine2') as any).value != '') {
          this.accountForm.form.controls.additionalBillingAddressLine.patchValue((document.querySelector('#FBAddressLine2') as any).value);
          this.accountForm.form.controls.additionalBillingAddressLine.markAsDirty();
          this.accountForm.form.controls.additionalBillingAddressLine.updateValueAndValidity();
        }
        if((document.querySelector('#FBCity') as any) && (document.querySelector('#FBCity') as any).value != '') {
          this.accountForm.form.controls.city.patchValue((document.querySelector('#FBCity') as any).value);
          this.accountForm.form.controls.city.markAsDirty();
          this.accountForm.form.controls.city.updateValueAndValidity();
        }
        if((document.querySelector('#FBZip') as any) && (document.querySelector('#FBZip') as any).value != '') {
          this.accountForm.form.controls.zipPostalCode.patchValue((document.querySelector('#FBZip') as any).value);
          this.accountForm.form.controls.zipPostalCode.markAsDirty();
          this.accountForm.form.controls.zipPostalCode.updateValueAndValidity();
        }

       // this.confirmAddress();
       this.validate_AddressAndCalculateTaxes('billing');
       this.onCountryChange();
      }
      setTimeout(() => {
        if (FBState[(document.querySelector('#FBStateCode') as any).value] != '') {
          const fbStateCode = (document.querySelector('#FBStateCode') as any)?.value;
          if (fbStateCode) {
            const stateValue = fbStateCode.length > 2 ? fbStateCode : FBState[fbStateCode];
            // Only set state if it exists in the FBState mapping
            if (Object.values(FBState).includes(stateValue)) {
              this.accountForm.form.controls.state.patchValue(stateValue);
            }
          }
          this.accountForm.form.controls.state.markAsDirty();
          this.accountForm.form.controls.state.updateValueAndValidity();

          // Remove any characters from phone that don't match the allowed pattern
          if ((document.querySelector('#phone') as any) && (document.querySelector('#phone') as any).value != '') {
            const phoneValue = (document.querySelector('#phone') as any).value;
            const hasInvalidChars = new RegExp(this.phonePattern).test(phoneValue);
            let cleanedValue = phoneValue;
            if (hasInvalidChars) {
              cleanedValue = phoneValue.replace(/[^-+*().,0-9x\/\# ]/g, '');
            }
            if (cleanedValue.length > 18) {
              cleanedValue = cleanedValue.substring(0, 18);
            }
            this.accountForm.form.controls.phone.patchValue(cleanedValue);
            this.accountForm.form.controls.phone.markAsDirty();
            this.accountForm.form.controls.phone.updateValueAndValidity();
          }
        }
        this.accountForm.form.markAsDirty();
      //  this.confirmAddress();
        this.validate_AddressAndCalculateTaxes('billing');
        this.onStateChange();

        // lets blur all inputs so the mobile keyboard closes
        try {
          document.querySelectorAll('input').forEach((input) => {
            (input as any).blur();
          });
        } catch (e) {
          console.log('[GH] error blurring inputs', e);
        }
      },300)
    } catch (e) {
      console.log('Facebook app state changed', e);
    }
  }

}
